import { API_BASE_URL } from '../config/api';
import { getAuthToken } from './auth';

/**
 * Fetch wrapper with authentication and error handling
 * @param {string} url - API endpoint URL
 * @param {object} options - Fetch options
 * @param {boolean} options.silent - If true, suppress console errors (useful for optional endpoints)
 */
async function fetchWithAuth(url, options = {}) {
  const { silent, ...fetchOptions } = options;

  const token = getAuthToken();
  if (!token) {
    throw new Error('No authentication token');
  }

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...fetchOptions,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
      },
    });

    if (response.status === 401) {
      // Handle unauthorized - token expired or invalid
      throw new Error('Session expired. Please log in again.');
    }

    // For 404 on optional endpoints, return null silently
    if (response.status === 404 && silent) {
      return null;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `Request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    // Suppress errors for optional endpoints
    if (silent && (error.message.includes('404') || error.message.includes('Not Found'))) {
      return null;
    }
    throw error;
  }
}

/**
 * Get dashboard usage statistics
 *
 * Returns:
 * - interviews_used: Number of interviews started this period
 * - interviews_limit: Total allowed (-1 = unlimited)
 * - period_start/end: Billing period dates
 * - difficulty_levels: Available formats (Quick/Medium/Hard)
 * - coach_modes: Available coach modes
 *
 * Note: Returns null if endpoint is not available (e.g., not deployed yet)
 */
export async function getDashboardUsage() {
  return fetchWithAuth('/api/dashboard/usage', { silent: true });
}

/**
 * Get overall performance analytics
 *
 * NOTE: Currently returns MOCK DATA
 * Real analytics require AI agent integration
 *
 * Returns:
 * - bars_scores: BARS scores for 8 competency scales
 * - average_rating: Overall rating (1-10)
 * - verdict: Overall verdict
 * - is_mock: true (indicates preview data)
 *
 * Note: Returns null if endpoint is not available (e.g., not deployed yet)
 */
export async function getOverallAnalytics() {
  return fetchWithAuth('/api/dashboard/analytics/overall', { silent: true });
}

/**
 * Get detailed analysis for a specific interview
 *
 * Args:
 *   interviewId: UUID of the interview
 *
 * Returns:
 * - rating: Overall rating (1-10)
 * - verdict: Performance verdict
 * - summary: Text summary
 * - strengths: List of strengths
 * - improvements: List of improvements
 * - bars_scores: BARS scale scores
 * - progress: Progress percentage
 * - is_completed: Completion status
 *
 * Note: Returns null if endpoint is not available (e.g., not deployed yet)
 */
export async function getInterviewAnalysis(interviewId) {
  return fetchWithAuth(`/api/dashboard/interviews/${interviewId}/analysis`, { silent: true });
}

/**
 * Get aggregated interview statistics for the cancellation/retention flow.
 */
export async function getCancellationStats() {
  return fetchWithAuth('/api/dashboard/cancellation-stats');
}

/**
 * Open Stripe Customer Portal (cancel, update card, view invoices)
 */
export async function openBillingPortal() {
  const res = await fetchWithAuth('/api/payments/portal', { method: 'POST' });
  if (!res || !res.portal_url) throw new Error('Failed to open billing portal');
  return res.portal_url;
}

/**
 * Delete all user interviews and reset profile data (final destructive step).
 */
export async function clearUserProfile() {
  return fetchWithAuth('/api/auth/profile/clear', {
    method: 'DELETE',
  });
}
